import { isAxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { chapterReportsApi } from '@/api/chapterReports'
import type {
  ChapterReportView,
  UpdateChapterReportInput,
} from '@/types/chapterReport'

export const chapterReportKeys = {
  all: ['lc', 'admin', 'chapter-reports'] as const,
  resolve: (learnerId: number, testId: number) =>
    [...chapterReportKeys.all, 'resolve', learnerId, testId] as const,
  detail: (learnerId: number, reportId: number) =>
    [...chapterReportKeys.all, 'detail', learnerId, reportId] as const,
}

interface BackendErrorPayload {
  error?: {
    code?: string
  }
}

export function chapterReportErrorCode(error: unknown): string | null {
  if (!isAxiosError<BackendErrorPayload>(error)) return null
  return error.response?.data?.error?.code ?? null
}

export function isChapterReportConflict(error: unknown): boolean {
  const code = chapterReportErrorCode(error)
  return code === 'CHAPTER_REPORT_EDIT_CONFLICT' || code === 'DIRECTOR_COMMENT_EDIT_CONFLICT'
}

export function useResolveChapterReport(learnerId: number, testId: number) {
  return useQuery({
    queryKey: chapterReportKeys.resolve(learnerId, testId),
    queryFn: () => chapterReportsApi.resolve(learnerId, testId),
    enabled: learnerId > 0 && testId > 0,
    staleTime: Infinity,
  })
}

export function useChapterReport(learnerId: number, reportId: number | undefined) {
  return useQuery({
    queryKey: chapterReportKeys.detail(learnerId, reportId ?? 0),
    queryFn: () => chapterReportsApi.get(learnerId, reportId as number),
    enabled: learnerId > 0 && reportId != null && reportId > 0,
    staleTime: 10_000,
  })
}

export function useUpdateChapterReport(learnerId: number, reportId: number) {
  const queryClient = useQueryClient()
  const queryKey = chapterReportKeys.detail(learnerId, reportId)
  return useMutation({
    mutationFn: (input: UpdateChapterReportInput) =>
      chapterReportsApi.update(learnerId, reportId, input),
    onSuccess: (saved) => {
      queryClient.setQueryData<ChapterReportView>(queryKey, (current) => {
        if (!current) return current
        return {
          ...current,
          version: saved.version,
          overallComment: saved.overallComment,
          recommendedLessonIds: saved.recommendedLessonIds,
        }
      })
    },
  })
}

interface UpdateAttendanceNoteVariables {
  attendanceId: number
  note: string | null
  expectedVersion: number
}

export function useUpdateChapterReportAttendanceNote(learnerId: number, reportId: number) {
  const queryClient = useQueryClient()
  const queryKey = chapterReportKeys.detail(learnerId, reportId)
  return useMutation({
    mutationFn: (variables: UpdateAttendanceNoteVariables) =>
      chapterReportsApi.updateAttendanceNote(
        learnerId,
        variables.attendanceId,
        variables.note,
        variables.expectedVersion,
      ),
    onSuccess: (saved) => {
      queryClient.setQueryData<ChapterReportView>(queryKey, (current) => {
        if (!current) return current
        return {
          ...current,
          classSessions: current.classSessions.map((session) =>
            session.attendanceId === saved.attendanceId
              ? {
                  ...session,
                  coachingNote: saved.note,
                  coachingNoteVersion: saved.version,
                }
              : session,
          ),
        }
      })
    },
  })
}
